/**
 * Shared helpers for client-facing Task resources: compartment accounts, classification and chart-form status.
 * Staff screens no longer build client Tasks (ENG-3764); the listener bots create them.
 */

import type { MedplumClient } from '@medplum/core';
import type { Meta, Patient, Reference, Task } from '@medplum/fhirtypes';

import { getTaskTypeCoding } from '../pages/tasks/services/taskFhirMapping';
import { EPISODE_OF_CARE_CONTEXT_EXTENSION } from './caseloadFhirConstants';
import {
  CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  CLIENT_DOCUMENT_COMPLETION_CODE,
  CLIENT_FACING_TASK_TAG_CODE,
  CLIENT_FACING_TASK_TAG_SYSTEM,
  CLIENT_FORM_ASSIGNMENT_CODE,
  CLIENT_SIGNATURE_REQUEST_CODE,
  CLIENT_TAKE_NOW_ASSESSMENT_CODE,
  RULES_ENGINE_CODE_SYSTEM,
  RULES_ENGINE_FORM_ASSIGNMENT_CODE,
  TASK_TYPE_CODE_SYSTEM,
} from './taskFhirConstants';

// ─── Compartment helpers ──────────────────────────────────────────────

function dedupeAccountRefs(refs: Reference[]): Reference[] {
  const seen = new Set<string>();
  const out: Reference[] = [];
  for (const r of refs) {
    const key = r.reference ?? '';
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/** Collect CareTeam (etc.) account refs from any resource `Meta` (Medplum `accounts` + legacy `account`). */
export function collectMetaAccounts(meta: Meta | undefined): Reference[] {
  if (!meta) return [];
  const plural = meta.accounts;
  if (plural?.length) {
    return dedupeAccountRefs(plural);
  }
  const single = meta.account;
  if (single?.reference) {
    return [single];
  }
  return [];
}

/** All compartment account refs on a Patient (dual `$set-accounts` → `meta.accounts`). */
export function collectPatientMetaAccounts(patient: Patient): Reference[] {
  return collectMetaAccounts(patient.meta);
}

/** Merge compartment account refs, preserving order and deduping by reference. */
export function mergeCompartmentAccountRefs(
  existing: Reference[],
  additions: Reference[],
): Reference[] {
  const seen = new Set<string>();
  const merged: Reference[] = [];
  for (const ref of [...existing, ...additions]) {
    const key = ref.reference;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    merged.push(ref);
  }
  return merged;
}

/** Copy Patient compartment refs onto a resource's meta.accounts (EoC, SR, Task, …). */
export function mergePatientAccountsOntoMeta(
  patient: Patient,
  meta: Meta | undefined,
): Meta {
  const accounts = collectPatientMetaAccounts(patient);
  if (accounts.length === 0) return meta ?? {};
  return { ...(meta ?? {}), accounts };
}

/**
 * Stamps resource meta.accounts for RBAC compartments after panel provisioning.
 * Merges patient compartments with the care-manager panel (and optional org).
 */
export function buildCompartmentMetaFromPatientAndPanel(
  patient: Patient,
  panelCareTeamId: string,
  organizationId?: string,
  existingMeta?: Meta,
): Meta {
  const additions: Reference[] = [{ reference: `CareTeam/${panelCareTeamId}` }];
  if (organizationId?.trim()) {
    additions.unshift({ reference: `Organization/${organizationId.trim()}` });
  }
  const accounts = mergeCompartmentAccountRefs(collectPatientMetaAccounts(patient), additions);
  if (accounts.length === 0) return existingMeta ?? {};
  return { ...(existingMeta ?? {}), accounts };
}

/**
 * Read Patient compartment refs for stamping Tasks / QuestionnaireResponses so
 * multi-CareTeam patients stay visible to every panel (Emily Rodriguez, Alex Rivera, …).
 */
export async function getPatientAccountRefs(
  medplum: MedplumClient,
  patientId: string,
): Promise<Reference[]> {
  const pt = await medplum.readResource('Patient', patientId);
  return collectPatientMetaAccounts(pt);
}

// ─── Utility readers ──────────────────────────────────────────────────

/** Extract the Questionnaire reference from a Task.focus. */
export function getQuestionnaireRefFromTask(task: Task): string | undefined {
  const ref = task.focus?.reference;
  if (!ref?.startsWith('Questionnaire/')) return undefined;
  return ref;
}

/** Extract the EpisodeOfCare reference from Task extensions. */
export function getTaskEpisodeRef(task: Task): string | undefined {
  return task.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXTENSION)
    ?.valueReference?.reference;
}

/** True if a Task is a client-facing questionnaire assignment (assessment or form). */
export function isClientFacingQuestionnaireTask(task: Task): boolean {
  if (!task.focus?.reference?.startsWith('Questionnaire/')) return false;

  const tags = task.meta?.tag ?? [];
  if (tags.some((t) => t.system === CLIENT_FACING_TASK_TAG_SYSTEM && t.code === CLIENT_FACING_TASK_TAG_CODE)) {
    return true;
  }

  const codings = task.code?.coding ?? [];
  if (codings.some((c) =>
    c.system === TASK_TYPE_CODE_SYSTEM && (
      c.code === CLIENT_FORM_ASSIGNMENT_CODE ||
      c.code === CLIENT_ASSESSMENT_ASSIGNMENT_CODE ||
      c.code === CLIENT_TAKE_NOW_ASSESSMENT_CODE
    ),
  )) {
    return true;
  }

  return false;
}

// ─── Canonical Task-kind classification ──────────────────────────────
//
// Assigned Assessments panel  → isAssessmentAssignmentTask
// Assessment Log              → isAssessmentAssignmentTask OR isTakeNowAssessmentTask
// Forms & Files               → isFormRequestTask
// Client Portal Tasks         → all client-facing (isClientFacingQuestionnaireTask)

/**
 * True if the Task is a one-time assessment assigned to the client (Assigned Assessments panel).
 * Uses CLIENT_ASSESSMENT_ASSIGNMENT_CODE.
 * Form requests and Take Now tasks are NOT assessment assignment tasks.
 */
export function isAssessmentAssignmentTask(task: Task): boolean {
  const codings = task.code?.coding ?? [];
  return codings.some(
    (c) => c.system === TASK_TYPE_CODE_SYSTEM && c.code === CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  );
}

/**
 * True if the Task is a provider-administered Take Now assessment.
 * Uses CLIENT_TAKE_NOW_ASSESSMENT_CODE.
 * Does NOT appear in Assigned Assessments; appears in Assessment Log after completion.
 */
export function isTakeNowAssessmentTask(task: Task): boolean {
  const codings = task.code?.coding ?? [];
  return codings.some(
    (c) => c.system === TASK_TYPE_CODE_SYSTEM && c.code === CLIENT_TAKE_NOW_ASSESSMENT_CODE,
  );
}

const TASK_DESCRIPTION_PREFIX = /^(?:complete|sign):\s*/i;

/** Human-readable form name for chart and Forms & Files (strips "Complete:" / "Sign:" prefixes). */
export function getFormTaskDisplayName(task: Task): string {
  const focusDisplay = task.focus?.display?.trim();
  if (focusDisplay) return focusDisplay;

  const description = task.description?.trim();
  if (description) {
    const stripped = description.replace(TASK_DESCRIPTION_PREFIX, '').trim();
    if (stripped) return stripped;
  }

  return getTaskTypeCoding(task)?.display ?? 'Form';
}

export type ChartFormTaskStatus = 'requested' | 'in-progress' | 'overdue' | 'completed' | 'cancelled';

/** Map a FHIR Task to a chart overview status label. */
export function resolveChartFormTaskStatus(task: Task): ChartFormTaskStatus {
  if (task.status === 'completed') return 'completed';
  if (task.status === 'cancelled') return 'cancelled';

  const dueStr = task.restriction?.period?.end?.slice(0, 10);
  if (dueStr && new Date(`${dueStr}T23:59:59`).getTime() < Date.now()) {
    return 'overdue';
  }
  if (task.status === 'in-progress') return 'in-progress';
  return 'requested';
}

export function chartFormTaskStatusLabel(status: ChartFormTaskStatus): string {
  switch (status) {
    case 'in-progress':
      return 'In progress';
    case 'overdue':
      return 'Overdue';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Requested';
  }
}

export function chartFormTaskStatusColor(status: ChartFormTaskStatus): string {
  switch (status) {
    case 'overdue':
      return 'red';
    case 'in-progress':
      return 'blue';
    case 'completed':
      return 'green';
    case 'cancelled':
      return 'gray';
    default:
      return 'yellow';
  }
}

/**
 * True if the Task is a form request (intake, consent, signature, etc.).
 * Covers manual form requests and Rules Engine form assignments.
 * Appears in Forms & Files and Client Portal; NOT in Assigned Assessments.
 */
export function isFormRequestTask(task: Task): boolean {
  const codings = task.code?.coding ?? [];
  return codings.some(
    (c) =>
      (c.system === TASK_TYPE_CODE_SYSTEM && (
        c.code === CLIENT_FORM_ASSIGNMENT_CODE ||
        c.code === CLIENT_SIGNATURE_REQUEST_CODE ||
        c.code === CLIENT_DOCUMENT_COMPLETION_CODE
      )) ||
      (c.system === RULES_ENGINE_CODE_SYSTEM && c.code === RULES_ENGINE_FORM_ASSIGNMENT_CODE),
  );
}

/** True if the Task is any kind of assessment (assigned or Take Now). */
export function isAnyAssessmentTask(task: Task): boolean {
  return isAssessmentAssignmentTask(task) || isTakeNowAssessmentTask(task);
}
